// src/pages/CompleteProfile.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Container,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Stack,
    Text,
    useToast,
    Flex,
    Image,
    InputGroup,
    InputRightElement,
    Avatar,
    Progress,
    Alert,
    AlertIcon,
    InputLeftAddon,
} from '@chakra-ui/react';
import { supabase } from '../config/supabaseClient';
import logo from '../assets/logo.png';

// Importe o PhoneInput e o estilo necessário
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css'; // Importe o estilo padrão
import '../styles/PhoneInput.css'; // Seu estilo customizado

// Importe o InputMask para formatação de CPF/CNPJ
import InputMask from 'react-input-mask';
import { cpf, cnpj } from 'cpf-cnpj-validator';

const CompleteProfile = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isCpf, setIsCpf] = useState(true);
    const [loadingCep, setLoadingCep] = useState(false);

    const [formData, setFormData] = useState({
        cpf_cnpj: '',
        phone: '',
        postal_code: '',
        address: '',
        address_number: '',
        city: '',
        state: '',
        neighborhood: '',
        complement: ''
    });

    const navigate = useNavigate();
    const toast = useToast();

    // Buscar dados do usuário
    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const { data: { session }, error: sessionError } = await supabase.auth.getSession();

                if (sessionError) throw sessionError;

                if (!session) {
                    navigate('/login');
                    return;
                }

                const { data: userData, error: userError } = await supabase.auth.getUser();

                if (userError) throw userError;
                if (!userData || !userData.user) {
                    navigate('/login');
                    return;
                }

                setUser(userData.user);

                // Verificar se já existe um perfil
                const { data: profileData, error: profileError } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('id', userData.user.id)
                    .single();

                if (profileError && profileError.code !== 'PGRST116') { // PGRST116 = not found, ignore this error
                    console.error('Erro ao buscar perfil:', profileError);
                }

                // Se o perfil já estiver completo (tem CPF/CNPJ), redirecionar para o dashboard
                if (profileData && profileData.cpf_cnpj) {
                    navigate('/dashboard');
                    return;
                }

                // Pré-preencher dados do perfil se existirem
                if (profileData) {
                    setFormData({
                        ...formData,
                        phone: profileData.phone || '',
                        cpf_cnpj: profileData.cpf_cnpj || '',
                        postal_code: profileData.postal_code || '',
                        address: profileData.address || '',
                        address_number: profileData.address_number || '',
                        city: profileData.city || '',
                        state: profileData.state || '',
                        neighborhood: profileData.neighborhood || '',
                        complement: profileData.complement || ''
                    });
                }

                // Pré-preencher telefone do usuário OAuth se existir
                if (userData.user.phone) {
                    setFormData(prev => ({
                        ...prev,
                        phone: userData.user.phone
                    }));
                }

            } catch (error) {
                console.error('Erro ao carregar dados do usuário:', error);
                toast({
                    title: 'Erro ao carregar dados',
                    description: 'Não foi possível carregar seus dados. Por favor, tente novamente.',
                    status: 'error',
                    duration: 5000,
                    isClosable: true
                });
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, [navigate, toast]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const toggleDocumentType = () => {
        setIsCpf(!isCpf);
        setFormData({
            ...formData,
            cpf_cnpj: '' // Limpa o campo quando troca o tipo
        });
    };

    const handleSearchCep = async () => {
        const cep = formData.postal_code?.replace(/\D/g, '');

        if (!cep || cep.length !== 8) {
            toast({
                title: 'CEP inválido',
                description: 'Por favor, digite um CEP válido com 8 dígitos.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setLoadingCep(true);

        try {
            const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
            const data = await response.json();

            if (data.erro) {
                toast({
                    title: 'CEP não encontrado',
                    description: 'O CEP informado não foi encontrado.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            setFormData(prev => ({
                ...prev,
                address: data.logradouro,
                neighborhood: data.bairro,
                city: data.localidade,
                state: data.uf,
            }));

            toast({
                title: 'Endereço encontrado',
                description: 'Os dados de endereço foram preenchidos automaticamente.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Erro ao buscar CEP:', error);
            toast({
                title: 'Erro na busca',
                description: 'Ocorreu um erro ao buscar o CEP. Tente novamente.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingCep(false);
        }
    };

    const validateForm = () => {
        // Validação de CPF/CNPJ usando a biblioteca
        if (formData.cpf_cnpj) {
            const isDocumentValid = isCpf
                ? cpf.isValid(formData.cpf_cnpj)
                : cnpj.isValid(formData.cpf_cnpj);

            if (!isDocumentValid) {
                toast({
                    title: isCpf ? 'CPF inválido' : 'CNPJ inválido',
                    description: `Por favor, digite um ${isCpf ? 'CPF' : 'CNPJ'} válido.`,
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                return false;
            }
        } else {
            toast({
                title: 'CPF/CNPJ obrigatório',
                description: 'Por favor, informe seu CPF ou CNPJ.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return false;
        }

        // Validação de telefone
        if (!formData.phone) {
            toast({
                title: 'Telefone obrigatório',
                description: 'Por favor, informe seu telefone.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return false;
        }

        // Se tiver o CEP preenchido, validar número
        if (formData.postal_code && formData.postal_code.replace(/\D/g, '').length === 8 && !formData.address_number) {
            toast({
                title: 'Número do endereço obrigatório',
                description: 'Se você preencheu o CEP, o número do endereço é obrigatório.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return false;
        }

        return true;
    };

    // Modificar a função handleSubmit no CompleteProfile.jsx
    // Atualizações para o handleSubmit no CompleteProfile.jsx

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setSaving(true);

        try {
            if (!user) throw new Error("Usuário não encontrado");

            // Adicione estes logs para depuração
            /*console.log("CompleteProfile - Submetendo dados:", {
                userId: user?.id,
                userEmail: user?.email,
                userName: user?.user_metadata?.name || user?.user_metadata?.full_name,
                formData
            });
            */

            let phoneNumber = formData.phone;
            if (phoneNumber && phoneNumber.startsWith('+')) {
                // Remove o código do país (ex: +55)
                phoneNumber = phoneNumber.substring(phoneNumber.indexOf(' ') + 1);
            }

            // Assegurar que temos os valores necessários
            const name = user.user_metadata?.full_name ||
                user.user_metadata?.name ||
                user.email.split('@')[0] ||
                'Usuário';

            // Adicione este log antes do upsert
            /*
            console.log("CompleteProfile - Dados para inserção:", {
                id: user.id,
                name: name,
                email: user.email,
                cpf_cnpj: formData.cpf_cnpj,
                phone: phoneNumber,
                // outros campos...
            });
            */

            // Atualizar perfil no supabase
            const { error } = await supabase
                .from('profiles')
                .upsert({
                    id: user.id,
                    name: name, // Garante que o nome nunca será nulo
                    email: user.email, // Garante que o email nunca será nulo
                    cpf_cnpj: formData.cpf_cnpj,
                    phone: phoneNumber,
                    postal_code: formData.postal_code,
                    address: formData.address,
                    address_number: formData.address_number,
                    city: formData.city,
                    state: formData.state,
                    neighborhood: formData.neighborhood,
                    complement: formData.complement,
                    provider: user.app_metadata?.provider || 'email',
                    creditos: 0 // Valor padrão para novos usuários
                }, { onConflict: 'id' });

            if (error) throw error;

            toast({
                title: 'Perfil atualizado com sucesso!',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            // Redirecionar para dashboard
            navigate('/dashboard');

        } catch (error) {
            console.error('Erro ao salvar perfil:', error);
            toast({
                title: 'Erro ao salvar dados',
                description: error.message || 'Ocorreu um erro ao salvar seu perfil. Tente novamente.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container maxW="md" py={12}>
                <Flex direction="column" align="center" justify="center" h="60vh">
                    <Progress size="xs" isIndeterminate w="80%" colorScheme="blue" mb={6} />
                    <Text color="gray.500">Carregando seus dados...</Text>
                </Flex>
            </Container>
        );
    }

    return (
        <Container maxW="md" py={12}>
            <Flex direction="column" align="center" mb={8}>
                <Image src={logo} alt="Draw Your Goods" height="60px" mb={4} />
                <Heading size="lg" textAlign="center" color="brand.primary">
                    Complete seu Perfil
                </Heading>
                <Text mt={2} textAlign="center" color="gray.600">
                    Precisamos de mais algumas informações para finalizar seu cadastro
                </Text>
            </Flex>

            {user && (
                <Box p={8} borderRadius="lg" boxShadow="md" bg="white">
                    {/* Informações do Google */}
                    <Flex align="center" mb={6} p={4} bg="gray.50" borderRadius="md">
                        <Avatar
                            src={user.user_metadata?.avatar_url || user.user_metadata?.picture}
                            name={user.user_metadata?.full_name || user.user_metadata?.name || user.email}
                            size="md"
                            mr={4}
                        />
                        <Box>
                            <Text fontWeight="bold">{user.user_metadata?.full_name || user.user_metadata?.name || 'Usuário'}</Text>
                            <Text fontSize="sm" color="gray.600">{user.email}</Text>
                            <Text fontSize="xs" color="gray.500">Conectado via {user.app_metadata?.provider || 'email'}</Text>
                        </Box>
                    </Flex>

                    <Alert status="info" mb={6} borderRadius="md">
                        <AlertIcon />
                        Precisamos de mais alguns dados para completar seu cadastro e habilitar todas as funcionalidades.
                    </Alert>

                    <form onSubmit={handleSubmit}>
                        <Stack spacing={4}>
                            <FormControl id="cpf_cnpj" isRequired>
                                <Flex justify="space-between" align="center">
                                    <FormLabel>{isCpf ? 'CPF' : 'CNPJ'}</FormLabel>
                                    <Button size="xs" onClick={toggleDocumentType} variant="outline">
                                        Mudar para {isCpf ? 'CNPJ' : 'CPF'}
                                    </Button>
                                </Flex>
                                <Input
                                    as={InputMask}
                                    mask={isCpf ? "999.999.999-99" : "99.999.999/9999-99"}
                                    name="cpf_cnpj"
                                    value={formData.cpf_cnpj}
                                    onChange={handleChange}
                                    placeholder={isCpf ? "000.000.000-00" : "00.000.000/0000-00"}
                                />
                            </FormControl>

                            <FormControl id="phone" isRequired>
                                <FormLabel>Telefone (WhatsApp)</FormLabel>
                                <Box className="phone-input-container">
                                    <PhoneInput
                                        international
                                        defaultCountry="BR"
                                        value={formData.phone}
                                        onChange={(value) => setFormData({
                                            ...formData,
                                            phone: value
                                        })}
                                        placeholder="(xx) xxxxx-xxxx"
                                    />
                                </Box>
                            </FormControl>

                            <FormControl id="postal_code">
                                <FormLabel>CEP (opcional)</FormLabel>
                                <InputGroup>
                                    <Input
                                        as={InputMask}
                                        mask="99999-999"
                                        name="postal_code"
                                        value={formData.postal_code}
                                        onChange={handleChange}
                                        placeholder="00000-000"
                                    />
                                    <InputRightElement width="4.5rem">
                                        <Button
                                            h="1.75rem"
                                            size="sm"
                                            onClick={handleSearchCep}
                                            isLoading={loadingCep}
                                            isDisabled={!formData.postal_code || formData.postal_code.replace(/\D/g, '').length !== 8}
                                        >
                                            Buscar
                                        </Button>
                                    </InputRightElement>
                                </InputGroup>
                            </FormControl>

                            {formData.postal_code && formData.postal_code.replace(/\D/g, '').length === 8 && (
                                <>
                                    <FormControl id="address">
                                        <FormLabel>Logradouro</FormLabel>
                                        <Input
                                            name="address"
                                            value={formData.address}
                                            onChange={handleChange}
                                            placeholder="Rua, Avenida, etc."
                                        />
                                    </FormControl>

                                    <Stack direction={{ base: "column", md: "row" }} spacing={4}>
                                        <FormControl id="address_number">
                                            <FormLabel>Número</FormLabel>
                                            <Input
                                                name="address_number"
                                                value={formData.address_number}
                                                onChange={handleChange}
                                                placeholder="123"
                                            />
                                        </FormControl>

                                        <FormControl id="complement">
                                            <FormLabel>Complemento</FormLabel>
                                            <Input
                                                name="complement"
                                                value={formData.complement}
                                                onChange={handleChange}
                                                placeholder="Apto, Bloco, etc."
                                            />
                                        </FormControl>
                                    </Stack>

                                    <FormControl id="neighborhood">
                                        <FormLabel>Bairro</FormLabel>
                                        <Input
                                            name="neighborhood"
                                            value={formData.neighborhood}
                                            onChange={handleChange}
                                            placeholder="Bairro"
                                        />
                                    </FormControl>

                                    <Stack direction={{ base: "column", md: "row" }} spacing={4}>
                                        <FormControl id="city">
                                            <FormLabel>Cidade</FormLabel>
                                            <Input
                                                name="city"
                                                value={formData.city}
                                                onChange={handleChange}
                                                placeholder="Cidade"
                                            />
                                        </FormControl>

                                        <FormControl id="state">
                                            <FormLabel>Estado</FormLabel>
                                            <Input
                                                name="state"
                                                value={formData.state}
                                                onChange={handleChange}
                                                placeholder="UF"
                                                maxLength={2}
                                            />
                                        </FormControl>
                                    </Stack>
                                </>
                            )}

                            <Button
                                type="submit"
                                colorScheme="blue"
                                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                                color="white"
                                size="lg"
                                w="100%"
                                mt={6}
                                isLoading={saving}
                                loadingText="Salvando..."
                                _hover={{
                                    transform: 'translateY(-2px)',
                                    boxShadow: 'lg',
                                }}
                            >
                                Concluir cadastro
                            </Button>
                        </Stack>
                    </form>
                </Box>
            )}
        </Container>
    );
};

export default CompleteProfile;